import type {
  IIncidentProvider,
  Incident,
  CreateIncidentParams,
  IncidentSeverity,
  IncidentStatus,
} from './types';

const EVENTS_URL = 'https://events.pagerduty.com/v2/enqueue';
const API_BASE = 'https://api.pagerduty.com';

const SEVERITY_MAP: Record<IncidentSeverity, 'critical' | 'error' | 'warning' | 'info'> = {
  critical: 'critical',
  high: 'error',
  warning: 'warning',
  info: 'info',
};

interface PagerDutyConfig {
  integrationKey: string;
  apiToken?: string;
  serviceId?: string;
}

const now = () => new Date().toISOString();

export class PagerDutyProvider implements IIncidentProvider {
  constructor(private config: PagerDutyConfig) {}

  async createIncident(params: CreateIncidentParams): Promise<Incident> {
    const id = `pd-${Date.now()}`;
    try {
      const body = {
        routing_key: this.config.integrationKey,
        event_action: 'trigger',
        dedup_key: id,
        payload: {
          summary: params.title,
          source: params.serviceKey ?? 'expai-mes',
          severity: SEVERITY_MAP[params.severity],
          custom_details: {
            description: params.description,
            ...(params.details ?? {}),
          },
        },
      };
      await fetch(EVENTS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (e) {
      console.warn('[pagerduty] enqueue failed', e);
    }
    return {
      id,
      title: params.title,
      description: params.description,
      severity: params.severity,
      status: 'triggered',
      serviceKey: params.serviceKey ?? 'expai-mes',
      createdAt: now(),
      updatedAt: now(),
    };
  }

  private async restPut(id: string, status: IncidentStatus): Promise<void> {
    if (!this.config.apiToken) return;
    try {
      await fetch(`${API_BASE}/incidents/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Token token=${this.config.apiToken}`,
          Accept: 'application/vnd.pagerduty+json;version=2',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ incident: { type: 'incident_reference', status } }),
      });
    } catch (e) {
      console.warn('[pagerduty] put failed', e);
    }
  }

  async acknowledgeIncident(id: string): Promise<Incident> {
    await this.restPut(id, 'acknowledged');
    return this.stub(id, 'acknowledged');
  }

  async resolveIncident(id: string): Promise<Incident> {
    await this.restPut(id, 'resolved');
    return this.stub(id, 'resolved');
  }

  async listIncidents(filter?: { status?: IncidentStatus; limit?: number }): Promise<Incident[]> {
    if (!this.config.apiToken) return [];
    try {
      const url = new URL(`${API_BASE}/incidents`);
      if (filter?.status) url.searchParams.set('statuses[]', filter.status);
      url.searchParams.set('limit', String(filter?.limit ?? 50));
      const res = await fetch(url.toString(), {
        headers: {
          Authorization: `Token token=${this.config.apiToken}`,
          Accept: 'application/vnd.pagerduty+json;version=2',
        },
      });
      if (!res.ok) return [];
      const json = await res.json();
      return (json.incidents ?? []).map((i: any) => this.fromApi(i));
    } catch (e) {
      console.warn('[pagerduty] list failed', e);
      return [];
    }
  }

  async getIncident(id: string): Promise<Incident | null> {
    if (!this.config.apiToken) return null;
    try {
      const res = await fetch(`${API_BASE}/incidents/${id}`, {
        headers: {
          Authorization: `Token token=${this.config.apiToken}`,
          Accept: 'application/vnd.pagerduty+json;version=2',
        },
      });
      if (!res.ok) return null;
      const json = await res.json();
      return this.fromApi(json.incident);
    } catch {
      return null;
    }
  }

  private fromApi(i: any): Incident {
    const sev = (i.urgency === 'high' ? 'high' : 'info') as IncidentSeverity;
    return {
      id: i.id,
      title: i.title ?? i.summary ?? '(sin título)',
      description: i.description ?? '',
      severity: sev,
      status: i.status as IncidentStatus,
      serviceKey: i.service?.id ?? 'pagerduty',
      createdAt: i.created_at ?? now(),
      updatedAt: i.last_status_change_at ?? now(),
      resolvedAt: i.resolved_at,
      assignee: i.assignments?.[0]?.assignee?.summary,
      url: i.html_url,
    };
  }

  private stub(id: string, status: IncidentStatus): Incident {
    return {
      id,
      title: `Incident ${id}`,
      description: '',
      severity: 'info',
      status,
      serviceKey: 'pagerduty',
      createdAt: now(),
      updatedAt: now(),
      resolvedAt: status === 'resolved' ? now() : undefined,
    };
  }
}
